import { useState, useRef } from "react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";
import { Button } from "../ui/button";
import type { Id } from "convex/_generated/dataModel";

interface FlyerUploadProps {
  onUpload: (storageId: Id<"_storage">) => void;
  storageId?: Id<"_storage"> | null;
  /** Use the public (no-auth) upload URL — required for unauthenticated users */
  isPublic?: boolean;
  /** Override the upload button label */
  label?: string;
}

export function FlyerUpload({ onUpload, storageId, isPublic, label }: FlyerUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateUploadUrl = useMutation(api.verification.generateUploadUrl);
  const generateUploadUrlPublic = useMutation(api.verification.generateUploadUrlPublic);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setError("Please upload a JPG, PNG, or WebP image");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be less than 10MB");
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      // Generate upload URL
      const uploadUrl = isPublic ? await generateUploadUrlPublic() : await generateUploadUrl();

      // Upload file
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!response.ok) throw new Error("Upload failed");

      const { storageId: newStorageId } = await response.json();

      // Create preview
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);

      onUpload(newStorageId);
    } catch (err) {
      setError("Upload failed. Please try again.");
      console.error("Upload error:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {preview || storageId ? (
        <div className="relative">
          {preview && (
            <img
              src={preview}
              alt="Flyer preview"
              className="w-full max-h-64 object-contain rounded-lg border border-border"
            />
          )}
          {!preview && storageId && (
            <div className="w-full h-32 bg-secondary/30 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handleRemove}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors"
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
              <span className="text-sm text-muted-foreground">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {label ?? "Click to upload event flyer"}
              </span>
              <span className="text-xs text-muted-foreground/60">
                JPG, PNG, or WebP (max 10MB)
              </span>
            </div>
          )}
        </button>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
